import { FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import type { PreviewImportOutput } from '../hooks/useFluxoGerencialMutations'
import { formatDateBR } from '../utils/dateUtils'

interface GerencialImportStep2Props {
  fileName: string
  fileSize: number
  preview: PreviewImportOutput
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const formatDate = formatDateBR

/**
 * Step 2 — preview before commit. Shows server-parsed counts (valid /
 * skipped / warnings) and the first 5 valid rows so the user knows exactly
 * what will be imported.
 *
 * The same parser runs at processImport time, so what the user sees here
 * matches the actual import outcome 1:1.
 */
export function GerencialImportStep2({ fileName, fileSize, preview }: GerencialImportStep2Props) {
  const { totalAttempted, validRows, skippedRows, warningRows, sample } = preview
  const allBad = validRows === 0

  return (
    <div className="space-y-4">
      {/* File card */}
      <div className="flex items-center gap-3 p-3 bg-brand-dark-lighter rounded-monumenta border border-brand-dark-lighter">
        <div className="w-10 h-10 rounded-monumenta bg-brand-primary/10 flex items-center justify-center shrink-0">
          <FileSpreadsheet className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white font-medium truncate">{fileName}</p>
          <p className="text-xs text-brand-gray mt-0.5">
            {(fileSize / 1024).toFixed(1)} KB · {totalAttempted} linha{totalAttempted === 1 ? '' : 's'} detectada{totalAttempted === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {/* Validation summary */}
      <div className="space-y-2">
        <p className="text-xs text-brand-gray uppercase tracking-wide font-semibold">Validação do servidor</p>

        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center gap-2 p-3 rounded-md bg-success/10 border border-success/30">
            <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
            <div className="text-sm text-white min-w-0">
              <div className="font-semibold">{validRows}</div>
              <div className="text-xs text-brand-gray">{validRows === 1 ? 'válida' : 'válidas'}</div>
            </div>
          </div>

          <div className={`flex items-center gap-2 p-3 rounded-md ${
            warningRows > 0
              ? 'bg-warning/10 border border-warning/30'
              : 'bg-brand-dark-lighter border border-brand-dark-lighter opacity-60'
          }`}>
            <AlertTriangle className={`w-4 h-4 shrink-0 ${warningRows > 0 ? 'text-warning' : 'text-brand-gray'}`} />
            <div className="text-sm text-white min-w-0">
              <div className="font-semibold">{warningRows}</div>
              <div className="text-xs text-brand-gray">com aviso</div>
            </div>
          </div>

          <div className={`flex items-center gap-2 p-3 rounded-md ${
            skippedRows > 0
              ? 'bg-error/10 border border-error/30'
              : 'bg-brand-dark-lighter border border-brand-dark-lighter opacity-60'
          }`}>
            <XCircle className={`w-4 h-4 shrink-0 ${skippedRows > 0 ? 'text-error' : 'text-brand-gray'}`} />
            <div className="text-sm text-white min-w-0">
              <div className="font-semibold">{skippedRows}</div>
              <div className="text-xs text-brand-gray">{skippedRows === 1 ? 'descartada' : 'descartadas'}</div>
            </div>
          </div>
        </div>

        {warningRows > 0 && (
          <p className="text-xs text-brand-gray italic">
            Linhas "com aviso" entram no banco, mas têm Natureza ou Centro de Resultado vazios — corrija no modo edição depois.
          </p>
        )}
        {skippedRows > 0 && (
          <p className="text-xs text-brand-gray italic">
            Linhas "descartadas" não serão importadas (faltam Data ou Valor).
          </p>
        )}
      </div>

      {/* Sample table */}
      {sample.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-brand-gray uppercase tracking-wide font-semibold">
            Amostra das primeiras {sample.length} linha{sample.length === 1 ? '' : 's'}
          </p>
          <div className="overflow-auto rounded-md border border-brand-dark-lighter bg-brand-dark">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-brand-dark-light border-b border-brand-dark-lighter">
                  <th className="px-2 py-1.5 text-left font-semibold text-brand-gray uppercase tracking-wide">Data</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-brand-gray uppercase tracking-wide">Memo</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-brand-gray uppercase tracking-wide">Valor</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-brand-gray uppercase tracking-wide">Natureza</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-brand-gray uppercase tracking-wide">C. Result.</th>
                </tr>
              </thead>
              <tbody>
                {sample.map((row, i) => {
                  const hasGap = row.Natureza === null || row.CentroResultado === null
                  return (
                    <tr
                      key={i}
                      className={`border-b border-brand-dark-lighter last:border-0 ${
                        hasGap ? 'bg-warning/5' : ''
                      }`}
                    >
                      <td className="px-2 py-1.5 text-white">{formatDate(row.Data)}</td>
                      <td className="px-2 py-1.5 text-white truncate max-w-[160px]" title={row.Memo ?? ''}>
                        {row.Memo || '—'}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono ${row.Valor < 0 ? 'text-error' : 'text-white'}`}>
                        {formatCurrency(row.Valor)}
                      </td>
                      <td className="px-2 py-1.5 text-white">
                        {row.Natureza || <span className="text-warning text-[10px] uppercase">vazio</span>}
                      </td>
                      <td className="px-2 py-1.5 text-white">
                        {row.CentroResultado ?? <span className="text-warning text-[10px] uppercase">vazio</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DRE warning OR fatal */}
      {allBad ? (
        <div className="p-3 rounded-md bg-error/10 border border-error/30 flex items-start gap-2">
          <XCircle className="w-4 h-4 text-error shrink-0 mt-0.5" />
          <div className="text-sm text-white">
            <p className="font-medium text-error">Nenhuma linha válida encontrada</p>
            <p className="text-xs text-brand-gray mt-0.5">
              Verifique o cabeçalho, o formato das datas e os valores antes de tentar de novo.
            </p>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-md bg-warning/10 border border-warning/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
          <div className="text-sm text-white">
            <p className="font-medium text-warning">Esta importação reprocessa o cache da DRE</p>
            <p className="text-xs text-brand-gray mt-0.5">
              Pode levar 30–60 segundos depois de confirmar. Não feche a aba.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
